/*!
 * es-desc-accordion.js — 商品描述折叠 + 富版式
 * 版本 9 · 2026-09-21
 * 适用：Shopify / Shrine PRO（Dawn 血统）。描述容器 = .product__description
 *
 * 安装（二选一，详见 主题折叠栏_安装指南.md）：
 *   A. 上传到 assets/ 后，在 layout/theme.liquid 的 </body> 前加两行
 *   B. 整段粘贴到商品模板的「Custom Liquid」区块（本文件不含 Liquid 语法，可直接粘）
 *
 * 可调项都在下面的 CFG 里，不用改别处。
 *
 * v2 相比 v1 新增「富版式」（CFG.rich = true）：
 *   Fitment              车型行 -> 圆角 chip 标签；说明句 -> 浅色小字
 *   Specifications       `标签: 值` -> 左标签右值卡片
 *   Product Code         纯料号 -> 等宽代码样式
 *   Shipping & After-sales  按关键字配图标行
 *   其它章节（Key Features / Package Includes / Installation Notes）保持项目符号不动
 *
 * v3 相比 v2 新增「语义标记兼容」（2026-09-18）：
 *   数据侧为了 SEO 改了 Body 的写法 —— 章节条目从 <p>• …</p> 改成：
 *     · <ul><li>…</li></ul>   其它章节（Fitment / Specifications / Shipping …）
 *     · <h3>…</h3>            Key Features 的条目
 *   本脚本同时兼容新旧两种写法（存量商品不必重刷），前端外观由 CSS 归一保持完全不变。
 *
 * v4 相比 v3 改了两处观感（2026-09-18）：
 *   ① Shipping & After-sales 各条统一字重 —— v2/v3 会把「冒号前的标签」包成 <strong>，
 *      可实际数据里绝大多数条目没有冒号，于是只有 Estimated delivery 一条被加粗，
 *      在一列同构的句子里显得突兀。现在整行同字重（数据侧自带 <strong> 时仍保留）。
 *   ② 分隔线改由 CSS 画上边线（见 .es-acc 规则）—— 保证第一块上方也有线。
 *
 * v5 相比 v4 新增「Shipping 分栏」（2026-09-18）：
 *   Shipping & After-sales 里的条目按关键字拆成 Delivery / Returns & Warranty 两个 tab。
 *   切换靠容器上的 data-tab 属性 + CSS，不用 radio/label —— 也就不需要 id，
 *   同一页出现两份描述区也各切各的（radio 方案会因 id 重复而只认第一份）。
 *   ⚠️ 拆不出两块（某一边为空）时自动退回 v4 的平铺图标行 —— 内容一个字都不丢。
 *
 * v6 相比 v5 只做减法（2026-09-18）：
 *   去掉折叠标题左侧的装饰小图标（原来 7 个章节各有一个 .es-hico）。
 *   连同 decorate()、ICONS 里那 7 条章节路径一起删掉 —— 全站零残留，
 *   想恢复得从 _backup/theme-v5-before-v6-* 取回整套文件。
 *   Shipping 行内的 5 个小图标（.es-ico）不受影响，仍按关键字生成。
 *   版本号：data-es-acc="6"（v5 是 "5"，v4 是 "4"，v3 及更早是 "1"）。
 *
 * v7 相比 v6 新增「Feature 图标卡」（2026-09-19）：
 *   Key Features 从「• 一行文字」升级成「图标 + 标题 + 副标题」的两行卡片。
 *   · 图标按关键字从 ICONS 里挑（FEAT 表），认不出就用 check 兜底 —— 永远不空着。
 *   · 标题/副标题来自同一行文字里的分隔符：`标题 — 副标题`（也认 | 和 –）。
 *     **没有分隔符就只渲染标题那一行** —— 所以存量的一行内容不会被写坏，
 *     只是暂时显示成「图标 + 加粗标题」的单行卡，等补上副标题自然变两行。
 *   ⚠️ 为什么不用 <strong> 标记标题：① 折平会丢掉加粗 ② 切不出副标题（没有分隔符）。
 *      别为了加粗去动标记 —— 标题本来就由 CSS 加粗。
 *   关掉这一层：把下面 CFG.featCards 改成 false，立刻退回 v6 的纯项目符号。
 *   版本号：data-es-acc="7"。
 *
 * v8 相比 v7 新增「Fitment 分组」（2026-09-19）：
 *   1. Fitment 里写成 `组标签 — 车型, 车型, …` 的条目渲染成「组标签 + 该组 chip 行」。
 *   2. 没写分隔符的行完全不受影响，次序与 v7 一致（排在最前）。
 *   关上：CFG.fitGroups = false。版本号：data-es-acc="8"。
 *
 * v9 相比 v8 只放宽「条目内容白名单」（2026-09-21）：
 *   ⚠️ 起因（线上实测）：那款点火线圈的 Fitment 明明写对了
 *      `FI (Fuel Injected) — Sniper 150, RS 150, …`，页面上却完全不分组。
 *      原因不是分隔符 —— 是那条 <li> 结尾多了一个 <br>：旧规则
 *      「li.children.length > 0 就整段放弃」把这一个换行符当成了"内容不合规"，
 *      于是**整段 Fitment 连折叠带富化一起退回原始列表**。
 *   ⚠️ 这个 <br> 不在导入数据里 —— 全量 1173 款从 xlsx/csv 生成的描述里，
 *      「li 含子元素」的出现次数是 0。它是**在 Shopify 后台富文本编辑器里手工改描述时
 *      自动产生的**（加粗变 <strong>、改色变 <span>、软换行变 <br>）。
 *      也就是说：只要你在后台手工编辑过某一段，那一段就会踩这个坑。
 *   ⇒ 现在条目内部允许「无信息量」的行内标签：
 *      BR WBR SPAN STRONG B EM I U SMALL MARK SUB SUP
 *      折平它们只丢"视觉强调"，一个字符都不会丢。
 *      ⚠️ 结构性标签（img / a / table / div / 嵌套 ul / iframe …）**仍然整段放弃**，
 *         保守原则不变 —— 宁可退回原始列表，也不猜。
 *   版本号：data-es-acc="9"。自查：页面源码搜 data-es-acc 应看到 "9"。
 *
 * v10 相比 v9 只改「Key Features 的图标怎么挑」（2026-09-21）：
 *   三件事一起做：
 *   1. 新增 8 个图标 —— motor 启动电机 / chip 电控电子 / fuel 燃油 / oil 机油润滑 /
 *      fan 冷却散热 / chain 链条与正时链 / gear 传动齿轮 / ruler 尺寸规格。
 *      依据 = 全量 668 张卡里 79 张落到兜底 check，按语义归类后得到的清单。
 *   2. 收紧 6 处泛词（它们把不相关的卡拽到同一个图标上，是"图标重复"的另一半原因）：
 *        clock  "Clock and Temperature Readout" 时钟被判成 CNC 加工 → 删；
 *        smooth "Smooth Inner Passage" 顺畅气道被判成手部纹理 → 删；
 *        speed  "charging speed" 命中仪表 → 收窄为 speedometer / speed-sensor；
 *        spring "Lowered Front Stance" 被判成活塞 → 从 piston 挪到 sliders（张力/前叉语义）；
 *        clear  "clearance / clear dirt" 命中外观 → 收窄为 clear + 屏幕/面板/镜片/镀层；
 *        body cover housing construction structure 一批泛形态词 → 从 cube 删掉，
 *               只留真正描述形态的（folding / ribbed / sealed / molded / airflow …）。
 *      ⚠️ 另有一半"兜底"根本不是缺图标，是**词形没覆盖**：模板写的是
 *         `Corrosion Resistance`（名词），而规则里只列了 `corrosion-resistant`（形容词）。
 *         → armor 改为 `resist(ant|ance)` 一词通吃，一并覆盖 heat / oil / wear / weather。
 *   3. 款内去重：同一商品的 Key Features 里，第二张算出相同图标的卡改用
 *      「下一个命中的规则」。开关 CFG.featUnique（默认开）。
 *      ⚠️ 去重只影响"挑哪个图标"，**卡片文案一个字都不动**。
 *      代价：第二张同类卡拿到的是"次匹配"图标，可能不如「重复但都对」精准 ——
 *      所以留了开关，觉得别扭就把 CFG.featUnique 改 false。
 *   ⚠️ FEAT 表**第一条命中即返回 ⇒ 顺序敏感**：越具体的"这是什么件"越靠前，
 *      尺寸（ruler）这类弱信号排最后，`designed for / position` 那种兜底再往后。
 *   版本号：data-es-acc="10"。自查：页面源码搜 data-es-acc 应看到 "10"。
 *
 * 安全原则：任一章节只要有一条内容对不上规则，就整段保持原样 —— 只做增强，绝不改写。
 */
(function () {
  'use strict';

  var CFG = {
    // 描述容器。Shrine PRO 输出 <div class="product__description rte ...">
    container: '.product__description',
    // 作为折叠标题的标签。新描述统一用 <h2>。
    // ⚠️ v3 起 Key Features 的条目也是 <h3>，所以放宽成 'h2, h3' 会有歧义 ——
    //    已加守卫：h3 及以下必须「文案命中 SEC 里的 7 个章节名」才认作章节标题。
    //    想让旧描述（当时用 h3 做小节标题）也折叠时可以放心改成 'h2, h3'。
    headingSel: 'h2',
    // 第一段默认展开
    openFirst: true,
    // 指定标题始终展开（按标题文字匹配，忽略大小写和多余空格）
    // 例：['fitment', 'shipping & after-sales']
    openTitles: [],
    // 折叠块 class
    cls: 'es-acc',
    // 富版式总开关。改 false 就退回 v1 的纯折叠（不动内容）
    rich: true,
    // Shipping & After-sales 是否拆成 Delivery / Returns & Warranty 两个 tab。
    // 拆不出两块时（某一边没有条目）自动退回平铺，不受这里影响。
    tabShipping: true,
    // Key Features 是否渲染成「图标 + 标题 + 副标题」卡片（v7）。
    // 改 false 立刻退回 v6 的项目符号写法，数据一个字都不用动。
    featCards: true,
    // 标题与副标题的分隔符，按顺序试，取第一个命中的（只切第一次出现）。
    // 写成 `标题 — 副标题` 即可；没有分隔符的条目就只渲染标题。
    featSep: /\s+[—–|]\s+/,
    // v10：同一商品的 Key Features 里是否避免两张卡用同一个图标。
    // 做法 = 第二张卡改用「下一个命中的规则」，**不动文案**。
    // 改 false 立刻退回 v9 的写法（只按第一条命中的规则挑图标，允许重复）。
    featUnique: true,
    // Fitment 是否按「组标签 — 车型, 车型」分组渲染（v8）。
    // 改 false 立刻退回 v7 的平铺 chip，数据一个字都不用动。
    fitGroups: true,
    // Fitment 的分组分隔符。⚠️ 故意比 featSep 少两个字符，两个都是实测得出的：
    //   ① 不收 `|` —— Key Features 里 `主标题 | 规格` 是常见写法，Fitment 里
    //      `A | B` 更像并列列举，认成组标签会切错。
    //   ② 不收普通短横线 `-` —— 全站唯一一条带 ` - ` 的 Fitment 条目是
    //      行 765 的皮带尺寸 `LIKE 125 ( 781.5 - 19.9 - 30 ).`，
    //      收了它就会被切成「标签 LIKE 125 ( 781.5」+ 两个假 chip。
    //      破折号 `—` / `–` 才是分组语义。
    fitSep: /\s+[—–]\s+/
  };

  var ATTR = 'data-es-acc';
  var RICH = 'data-es-rich';

  // 章节识别：h2 文案 -> key（大小写/多余空格不敏感）
  var SEC = {
    'key features': 'features',
    'product code': 'code',
    'fitment': 'fitment',
    'specifications': 'specs',
    'package includes': 'package',
    'installation notes': 'install',
    'shipping & after-sales': 'shipping'
  };

  // 图标（纯装饰，aria-hidden）。全部是 24×24 的线条图标：
  // 只给 path/circle 的几何数据，描边粗细/颜色/尺寸统一由 CSS 决定 ——
  // 所以想换配色只改 CSS 一处，不用碰这里的路径。
  // key 名对应 SHIP 与 FEAT 里的 ic 字段；顺序也有意义。
  var ICONS = {
    // --- Shipping & After-sales 用（v2 起）---
    truck: '<path d="M2 6.6h11V15H2z"/><path d="M13 9.6h4.3l2.7 3V15h-7z"/><circle cx="6" cy="17.4" r="1.7"/><circle cx="16.8" cy="17.4" r="1.7"/>',
    cash: '<rect x="2.4" y="6.4" width="19.2" height="11.2" rx="2"/><circle cx="12" cy="12" r="2.6"/><path d="M5.8 9.4v5.2M18.2 9.4v5.2"/>',
    ret: '<path d="M20.2 11.6a8.2 8.2 0 1 1-2.6-5.9"/><path d="M20.6 4.6v5.6H15"/>',
    chat: '<path d="M21 5.9v9.2a1.5 1.5 0 0 1-1.5 1.5H8.6L3.4 20.4V7.4a1.5 1.5 0 0 1 1.5-1.5h14.6A1.5 1.5 0 0 1 21 5.9z"/>',
    shield: '<path d="M12 3.2l7 2.8v5.5c0 4.3-2.9 7.6-7 9.4-4.1-1.8-7-5.1-7-9.4V6z"/><path d="M9.1 12.2l2.2 2.2 4.2-4.4"/>',

    // --- Key Features 用（v7 起）---
    // cog 精密加工：外圈 8 个齿 + 内孔
    cog: '<circle cx="12" cy="12" r="3.3"/><path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7"/>',
    // armor 耐久防护：素面盾
    armor: '<path d="M12 3.2l7 2.7v5.4c0 4.2-2.9 7.4-7 9.1-4.1-1.7-7-4.9-7-9.1V5.9z"/>',
    // zap 电气点火：闪电
    zap: '<path d="M13.4 2.6L4.6 13.6h6.1l-.7 7.8 8.8-11h-6.1z"/>',
    // feather 轻量：羽毛
    feather: '<path d="M19.6 4.4a5.6 5.6 0 0 0-7.9 0L4.8 11.3V19.2h7.9l6.9-6.9a5.6 5.6 0 0 0 0-7.9z"/><path d="M16.2 7.8L4.8 19.2"/><path d="M17.4 14.6H9.6"/>',
    // layers 材料构成：叠层
    layers: '<path d="M12 3.2l8.6 4.3-8.6 4.3-8.6-4.3z"/><path d="M3.4 12.2l8.6 4.3 8.6-4.3"/><path d="M3.4 16.5l8.6 4.3 8.6-4.3"/>',
    // sliders 可调：三根滑轨
    sliders: '<path d="M4 7.2h16M4 12h16M4 16.8h16"/><circle cx="9.2" cy="7.2" r="2.1"/><circle cx="15" cy="12" r="2.1"/><circle cx="8" cy="16.8" r="2.1"/>',
    // wrench 安装装配：扳手
    wrench: '<path d="M15.4 3.8a4.7 4.7 0 0 0 5.7 5.7L10 20.6a2.7 2.7 0 0 1-3.8-3.8z"/><path d="M6.2 16.8l1 1"/>',
    // grip 触感纹理：九点阵列
    grip: '<circle cx="8" cy="7" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="7" r="1.5" fill="currentColor" stroke="none"/><circle cx="16" cy="7" r="1.5" fill="currentColor" stroke="none"/><circle cx="8" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="16" cy="12" r="1.5" fill="currentColor" stroke="none"/><circle cx="8" cy="17" r="1.5" fill="currentColor" stroke="none"/><circle cx="12" cy="17" r="1.5" fill="currentColor" stroke="none"/><circle cx="16" cy="17" r="1.5" fill="currentColor" stroke="none"/>',
    // sparkle 外观表面：一大一小两个星芒
    sparkle: '<path d="M11 3.4l1.8 4.8 4.8 1.8-4.8 1.8L11 16.6 9.2 11.8 4.4 10l4.8-1.8z"/><path d="M17.8 15.2l.9 2.2 2.2.9-2.2.9-.9 2.2-.9-2.2-2.2-.9 2.2-.9z"/>',
    // cube 结构形态：立方体
    cube: '<path d="M12 3.2l8.4 4.4v8.8L12 20.8l-8.4-4.4V7.6z"/><path d="M3.6 7.6L12 12l8.4-4.4M12 12v8.8"/>',
    // droplet 可清洗/过滤：水滴
    droplet: '<path d="M12 3.2c3.3 4.1 5.6 6.8 5.6 9.6a5.6 5.6 0 0 1-11.2 0c0-2.8 2.3-5.5 5.6-9.6z"/>',
    // lock 闭合防护：挂锁
    lock: '<rect x="4.6" y="10.4" width="14.8" height="9.8" rx="2"/><path d="M8.2 10.4V7.6a3.8 3.8 0 0 1 7.6 0v2.8"/>',
    // gauge 显示仪表：速度表
    gauge: '<path d="M3.9 17.2a8.7 8.7 0 1 1 16.2 0"/><path d="M12 17.2l4.2-5.6"/><circle cx="12" cy="17.2" r="1.3" fill="currentColor" stroke="none"/>',
    // list 含件清单：列表
    list: '<path d="M9.4 6.6h10.4M9.4 12h10.4M9.4 17.4h10.4"/><circle cx="4.9" cy="6.6" r="1.15" fill="currentColor" stroke="none"/><circle cx="4.9" cy="12" r="1.15" fill="currentColor" stroke="none"/><circle cx="4.9" cy="17.4" r="1.15" fill="currentColor" stroke="none"/>',
    // piston 动力机构：活塞
    piston: '<rect x="7.6" y="3.4" width="8.8" height="7.4" rx="1.4"/><path d="M12 10.8v3.6"/><path d="M8.2 19l3.8-4.6 3.8 4.6z"/>',
    // --- v10 新增：Key Features 用（按兜底清单归类得来）---
    // motor 启动电机：工程符号「圈里一个 M」，与细辐条的 cog 一眼分得开
    motor: '<circle cx="12" cy="12" r="7.4"/><path d="M9.6 15.4V8.6l4.8 6.8V8.6"/>',
    // chip 电控电子：CPU 芯片（方体 + 八只脚）
    chip: '<rect x="7.4" y="7.4" width="9.2" height="9.2" rx="1.6"/><path d="M10.2 7.4V4.4M13.8 7.4V4.4M10.2 19.6v-3M13.8 19.6v-3M7.4 10.2H4.4M7.4 13.8H4.4M19.6 10.2h-3M19.6 13.8h-3"/>',
    // fuel 燃油：加油机（油泵）
    fuel: '<path d="M4 20.2V5.8a1.8 1.8 0 0 1 1.8-1.8h5.6a1.8 1.8 0 0 1 1.8 1.8v14.4"/><path d="M2.6 20.2h12.8"/><path d="M13.2 9.2h2.6a1.8 1.8 0 0 1 1.8 1.8v6.4a1.4 1.4 0 0 0 2.8 0V9.6l-1.7-2"/>',
    // oil 机油润滑：油壶（壶身 + 壶盖 + 出油嘴）
    oil: '<rect x="3.6" y="12.4" width="11.6" height="7" rx="2"/><path d="M15.2 13.8l4.6-2.6"/><path d="M6.6 12.4V9.6h4.2l2.6 2.8"/>',
    // cool 冷却散热：雪花（六芒 + 小的分叉，比风扇叶片更干净）
    cool: '<path d="M12 3.6v16.8"/><path d="M4.9 7.8l14.2 8.4"/><path d="M19.1 7.8L4.9 16.2"/><path d="M9.4 5.4L12 8l2.6-2.6"/><path d="M9.4 18.6L12 16l2.6 2.6"/>',
    // chain 链条与正时链：两环相扣
    chain: '<path d="M9.4 14.6l5.2-5.2"/><path d="M8.2 12.2l-2 2a3 3 0 0 0 4.2 4.2l2-2"/><path d="M15.8 11.8l2-2a3 3 0 0 0-4.2-4.2l-2 2"/>',
    // gear 传动齿轮：实心轮廓的八齿齿轮（与 cog 的"细辐条"刻意区分）
    gear: '<path d="M10.9 3.4h2.2l.5 2.1 1.7.7 1.8-1.2 1.6 1.6-1.2 1.8.7 1.7 2.1.5v2.2l-2.1.5-.7 1.7 1.2 1.8-1.6 1.6-1.8-1.2-1.7.7-.5 2.1h-2.2l-.5-2.1-1.7-.7-1.8 1.2-1.6-1.6 1.2-1.8-.7-1.7-2.1-.5v-2.2l2.1-.5.7-1.7-1.2-1.8 1.6-1.6 1.8 1.2 1.7-.7z"/><circle cx="12" cy="12" r="2.6"/>',
    // ruler 尺寸规格：横放的直尺
    ruler: '<rect x="2.6" y="8.8" width="18.8" height="6.4" rx="1.4"/><path d="M6.6 8.8v2.2M10 8.8v3.2M13.4 8.8v2.2M16.8 8.8v3.2"/>',
    // check 兜底：认不出关键字时用它，绝不出现空白图标位
    check: '<circle cx="12" cy="12" r="8.6"/><path d="M8.4 12.3l2.5 2.5 4.7-4.9"/>'
  };

  // 售后 4~6 行的图标映射（按关键字，顺序有意义）
  var SHIP = [
    { re: /estimated delivery/i, ic: 'truck' },
    { re: /cash on delivery|\(\s*cod\s*\)/i, ic: 'cash' },
    { re: /return/i, ic: 'ret' },
    { re: /contact us/i, ic: 'chat' },
    { re: /warranty|guarantee|factory-tested/i, ic: 'shield' }
  ];

  // Key Features 每条文案 -> 图标的映射（v7 建立，v10 大改）。
  // ⚠️ 顺序有意义：从上往下第一个命中的胜出。
  //    排序原则 = **先判「这是什么件/什么系统」，后判「它有什么属性」**：
  //      具体系统（点火/启动/电控/燃油/冷却/链条/传动）→ 显示 → 耐久/材质 →
  //      可调/安装/触感/外观/形态 → 动力机构 → 尺寸 → 清单 → 位置兜底。
  //    「尺寸」这种弱信号必须排最后，否则 57mm / 12V / 40W 会把有明确语义的卡抢走。
  // 想加关键词就直接往对应行里补，不用动别处；加新图标 = ICONS 加一行 path + 这里加一条。
  // ⚠️ 三个字母的缩写必须加 \b（词边界）。踩过的坑：
  //    裸写 LED 会命中 "S[eal]ed" / "Mold[ed]" 这类词，把 Sealed housing 判成仪表图标。
  var FEAT = [
    // ==== 第 1 段：这是什么件 / 什么系统 ====
    { re: /ignition|\bCDI\b|rectification|Hall-effect|E-core|DLC-coated|\bspark|\bcoil|conversion/i, ic: 'zap' },
    // v10 启动/发电（Bendix、起动机、曲轴拖动、充电总成、强磁电机）
    { re: /\bbendix\b|\bstarter\b|cranking|charging assembly|strong-magnet|low-power motor/i, ic: 'motor' },
    // v10 电控电子（ECU/ECM、PGM-FI、线束、牵引力控制、电压规格）
    { re: /\bECU\b|\bECM\b|PGM-FI|engine control|electronic control|epoxy-pott|harness|wiring|traction control|\bTCS\b|voltage-specific/i, ic: 'chip' },
    // v10 燃油。⚠️ 负向先行排除 `fuel gauge` / `fuel level` —— 那是仪表，得留给 gauge
    { re: /\bfuel\b(?!\s*(gauge|level))|fuel-injector|two-port fuel|\binjector/i, ic: 'fuel' },
    // v10 冷却散热（"管理热量"原来被 cube 抢走）
    { re: /cooling fan|cooling fin|coolant|cooling (circuit|system)|heat[- ]dissipation|operating heat/i, ic: 'cool' },
    // v10 链条 / 正时链
    { re: /timing[- ]chain|chain[- ](guide|path|tension|bridge)|\bchain\b/i, ic: 'chain' },
    // v10 传动齿轮
    { re: /sprocket|\bgear(s|box)?\b|gear-driven|transmission|final-drive|drive face|cvt drive/i, ic: 'gear' },

    // ==== 第 2 段：显示 / 灯光 ====
    // ⚠️ v10 收窄 `speed`：裸词会命中 "charging speed"（充电速度）被当成仪表。
    //    同时补 beam / illuminat / flash / dimming，接管原来落兜底的灯光类文案。
    { re: /\bLED\b|\bLCD\b|\bRPM\b|display|backlight|indicator|speedometer|speed[\s-]?sensor|odometer|fuel gauge|fuel-level|warning|sequential|tachometer|voltmeter|\bbeam\b|illuminat|flash|dimming|\bclock\b|temperature|\blamp\b|dimmer/i, ic: 'gauge' },

    // ==== 第 3 段：耐久与材质 ====
    // ⚠️ v10 把列举的形容词换成 `resist(ant|ance)` 一词通吃：
    //    模板里写的是 `Corrosion Resistance`（名词），旧的 `corrosion-resistant` 一条都命中不了 ——
    //    这是"落兜底"的最大单一原因（全量 79 条里占两成）。
    { re: /resist(ant|ance)|reinforced|withstand/i, ic: 'armor' },
    // v10 机油润滑。⚠️ 必须排在 armor 之后 —— 否则 `Heat and Oil Resistance` 会被 oil 抢走
    { re: /\boil\b|lubricat|oil seal|oil pump/i, ic: 'oil' },
    { re: /lightweight|light-weight|lightened|reduced .{0,14}mass/i, ic: 'feather' },
    // ⚠️ v10 删掉 `clock` —— "Clock and Temperature Readout" 是仪表功能，不是 CNC 加工
    { re: /CNC|machined|anodized|forged|hardened|heat-treated|billet/i, ic: 'cog' },
    { re: /copper|iron-core|steel|alloy|aluminum|acrylic|resin|Kevlar|ceramic|fiberglass|platinum|iridium|braided|mesh|solid|thick/i, ic: 'layers' },

    // ==== 第 4 段：可调 / 安装 / 触感 / 外观 / 形态 ====
    // ⚠️ v10 把 `spring` 从 piston 挪到这里（原来 "Lowered Front Stance" 被判成活塞），
    //    并补上 fork / stance / lowering / ride height 这类前叉语义。
    { re: /adjustable|selectable|progressive|dual-band|sliding|adjust|tension|variable|fork|stance|lowering|ride height|\bspring/i, ic: 'sliders' },
    { re: /pre-drilled|pre-wired|adhesive|plug-in|installation|mounting|clamp|bracket|terminal|connector/i, ic: 'wrench' },
    // ⚠️ v10 删掉 `smooth` —— "Smooth Inner Passage" 说的是气道顺滑，不是握感纹理
    { re: /texture|textured|grip|knurled|embossed|rubber|friction|\bfirm/i, ic: 'grip' },
    // ⚠️ v10 收窄 `clear`：裸词会命中 `clearance`（间隙）与 "clear dirt"（清扫）
    { re: /\bclear\b(?=\s*(screen|panel|lens|finish|coat|tpu|acrylic))|transparent|translucent|see-through|finish|two-tone|smoked|decorative|color|appearance|\bview\b|windshield|silhouette/i, ic: 'sparkle' },
    // ⚠️ v10 删掉 body / cover / housing / construction / structure 五个泛形态词
    //    （"CVT Component Coverage" 这类外套件被它们一律判成"立方体"）
    { re: /folding|roller-type|ball-bearing|ribbed|ventilated|airflow|sealed|molded|flexible|h-beam/i, ic: 'cube' },
    { re: /washable|removable|clean(ing)?|rinsing|filter/i, ic: 'droplet' },
    // v10 补 `keyed`：Keyed Non-ABS Configuration 这类"钥匙系统"描述原来落兜底
    { re: /key-operated|keyed|closure|key-switch|guard|protection|protect/i, ic: 'lock' },
    // ⚠️ v10 从这条删掉 `spring`（见上）；补 crankshaft（曲轴总成）
    { re: /cam|tuning|performance|clutch|variator|throttle|carburetor|piston|valve|rocker|bushing|ramp plate|slot|jet|needle|hydraulic|pulley|crankshaft|rotating assembly/i, ic: 'piston' },

    // ==== 第 5 段：弱信号与兜底 ====
    // v10 尺寸规格：弱信号，必须排在所有语义规则之后
    { re: /\d+\s*(?:mm|cm|inch|tooth|degree|w|v)\b|wire-size|step-up|diameter|dimension|\bsize\b/i, ic: 'ruler' },
    { re: /includes|package|pairs|\bset\b|\bkit\b/i, ic: 'list' },
    // 最兜底的两条：位置/适配类描述（如 Designed for the … area）
    { re: /designed for|position/i, ic: 'wrench' }
  ];

  // 一条都没命中时用它 —— 宁可图标不精准，也不要留空白位。
  var FEAT_FALLBACK = 'check';

  // v5：Shipping 拆栏。数组顺序 = 界面上从左到右的顺序，第一个是默认选中的。
  var TAB = [
    { key: 'del', label: 'Delivery' },
    { key: 'aft', label: 'Returns & Warranty' }
  ];

  // ⚠️ 判据用「售后词」而不是「物流词」：Cash on Delivery 那行里也含 delivery，
  //    拿物流词去判会把退换货那几行一并收进 Delivery。命中这里 → Returns，其余 → Delivery。
  var AFT_RE = /return|refund|exchange|warranty|guarantee|factory-test|defect|repair|replace|contact\s+us|after-sales/i;

  // ---------- 基础工具 ----------

  function norm(s) {
    return String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
  }

  function isHeading(el) {
    if (!el || el.nodeType !== 1) return false;
    if (!/^H[1-6]$/.test(el.tagName)) return false;
    if (typeof el.matches !== 'function') return false;
    if (!el.matches(CFG.headingSel)) return false;
    // v3 守卫：Key Features 的条目现在也是 <h3>。
    // 如果哪天把 headingSel 放宽到 'h2, h3'（为了折叠旧描述），这些条目会被误当章节标题
    // → 一段 Key Features 会被碎成 N 个折叠块。所以 h3 及以下必须「文案是已知章节名」才算标题。
    if (el.tagName !== 'H1' && el.tagName !== 'H2') {
      return !!SEC[norm(el.textContent).toLowerCase()];
    }
    return true;
  }

  function mk(tag, cls, text) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.appendChild(document.createTextNode(text));
    return n;
  }

  function mkIcon(name) {
    var s = mk('span', 'es-ico');
    s.setAttribute('aria-hidden', 'true');
    s.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
      (ICONS[name] || '') + '</svg>';
    return s;
  }

  var BULLET = /^[\u2022\u00b7\u2023\u25aa\u25cf]\s*/;

  function stripBullet(s) {
    return s.replace(BULLET, '');
  }

  function stripDot(s) {
    return s.replace(/\.\s*$/, '');
  }

  // 条目内部允许出现的标签（v9）。折平它们只丢视觉强调，一个字符都不会丢：
  //   BR / WBR                     纯换行
  //   SPAN / STRONG / B / EM / I / U   后台富文本编辑器加粗、斜体、改色时产生
  //   SMALL / MARK / SUB / SUP     少见的行内修饰
  // 不在这张表里的（img / a / table / div / 嵌套 ul / iframe …）一律当作"结构"，
  // 触发整段放弃 —— 折平它们会真丢东西（图片、链接目标、表格结构）。
  var FLAT_OK = {
    BR: 1, WBR: 1,
    SPAN: 1, STRONG: 1, B: 1, EM: 1, I: 1, U: 1,
    SMALL: 1, MARK: 1, SUB: 1, SUP: 1
  };

  // 取条目的纯文本：只允许 FLAT_OK 里的后代标签，出现别的就返回 null（调用方整段放弃）。
  // ⚠️ 查的是**全部后代**而不是直接子元素 —— 像 <li><strong><img></strong></li>
  //    这种藏在两层里的也得拦住。
  function flatText(el) {
    var all = el.getElementsByTagName('*');
    for (var i = 0; i < all.length; i++) {
      if (!FLAT_OK[all[i].tagName]) return null;
    }
    return stripBullet(norm(el.textContent));
  }

  // 正文取条目文本。支持三种写法（2026-09 起数据侧改用后两种）：
  //   ① <p>…</p>                   旧格式 —— 已导入的存量商品
  //   ② <h3>…</h3>                 新格式 —— Key Features 的条目
  //   ③ <ul><li>…</li>…</ul>       新格式 —— 其它章节的条目
  // 条目自身不是这三种标签、或内部混了结构性标签 → 返回 null，整段放弃（保守原则不变）。
  function entries(nodes) {
    var a = [];
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      if (!n || n.nodeType !== 1) return null;
      var tag = n.tagName;
      var t;
      if (tag === 'UL' || tag === 'OL') {
        for (var j = 0; j < n.children.length; j++) {
          var li = n.children[j];
          if (li.tagName !== 'LI') return null;
          t = flatText(li);
          if (t === null) return null;
          a.push(t);
        }
      } else if (tag === 'P' || tag === 'H3') {
        t = flatText(n);
        if (t === null) return null;
        a.push(t);
      } else {
        // 混进了表格/图片/自定义容器等 —— 整段不碰，避免误伤
        return null;
      }
    }
    return a.length ? a : null;
  }

  // ---------- 逐章节构造 ----------

  // v8：Fitment 分组。
  //   条目里出现 ` — `（破折号两侧有空格）→ 拆成 { label, items }。
  //   任何一处不合规就返回 null，交回普通路径（普通路径最差也只是变成一行说明句），
  //   所以这里**永远不会把内容弄丢**。
  function splitFitGroup(s) {
    if (!CFG.fitGroups || !CFG.fitSep) return null;
    var m = CFG.fitSep.exec(s);
    if (!m) return null;
    // 分隔符必须在行内 —— 贴行首（没有标签）或贴行尾（没有车型）都不算分组
    if (m.index <= 0 || m.index + m[0].length >= s.length) return null;

    // 标签前多余的 `FIT FOR ：` 去掉 —— 章节名已经叫 Fitment，重复一遍纯属噪音。
    // 只在整段前缀命中时剥掉，不会碰到 `FIT FOR` 出现在标签后半段的情况。
    var label = norm(s.slice(0, m.index).replace(/^\s*fit\s*for\s*[:\uff1a]?\s*/i, ''));
    label = stripDot(label);
    // 标签太长的多半是说明句里恰好有个破折号，不做分组以免误伤
    if (!label || label.length > 60) return null;

    // 右侧按半角逗号分段；不用逗号就整段当一个 chip
    var parts = s.slice(m.index + m[0].length).split(',');
    var items = [];
    for (var i = 0; i < parts.length; i++) {
      var t = stripDot(norm(parts[i]));
      if (t) items.push(t);
    }
    if (!items.length) return null;
    return { label: label, items: items };
  }

  // Fitment：车型行 -> chip，其余（说明句 / 带冒号的年份行）-> 小字
  //   v8：带 ` — ` 的行 -> 「组标签 + 该组 chip 行」；未分组的行次序与 v7 一致（排在最前）。
  function buildFitment(list) {
    var chips = [];
    var notes = [];
    var groups = [];
    var i, j, t, g;

    for (i = 0; i < list.length; i++) {
      g = splitFitGroup(list[i]);
      if (g) { groups.push(g); continue; }
      t = stripDot(list[i]);
      if (t && t.length <= 50 && !/[?;\uff1b\uff1a:]/.test(t) && t.split(/\s+/).length <= 6) {
        chips.push(t);
      } else {
        notes.push(list[i]);
      }
    }

    // 两组都空 → 整段放弃（保持 v7 行为：宁可原样，也不要一个空壳）
    if (!chips.length && !groups.length) return null;

    var box = mk('div', 'es-fit');
    var row;

    if (chips.length) {
      row = mk('div', 'es-chips');
      for (i = 0; i < chips.length; i++) row.appendChild(mk('span', 'es-chip', chips[i]));
      box.appendChild(row);
    }

    for (i = 0; i < groups.length; i++) {
      var grp = mk('div', 'es-fit-group');
      grp.appendChild(mk('div', 'es-fit-glabel', groups[i].label));
      row = mk('div', 'es-chips');
      for (j = 0; j < groups[i].items.length; j++) {
        row.appendChild(mk('span', 'es-chip', groups[i].items[j]));
      }
      grp.appendChild(row);
      box.appendChild(grp);
    }

    for (i = 0; i < notes.length; i++) box.appendChild(mk('p', 'es-note', notes[i]));
    return box;
  }

  // Specifications：`标签: 值` -> 左标签右值卡片
  function buildSpecs(list) {
    var rows = [];
    var notes = [];
    for (var i = 0; i < list.length; i++) {
      var m = /^([^:\uff1a]{1,40})\s*[:\uff1a]\s*(\S[\s\S]*)$/.exec(list[i]);
      if (m) rows.push([stripDot(m[1]), m[2]]);
      else notes.push(list[i]);
    }
    if (!rows.length) return null;
    var box = mk('div', 'es-specs-box');
    var dl = mk('dl', 'es-specs');
    for (i = 0; i < rows.length; i++) {
      var r = mk('div', 'es-spec');
      r.appendChild(mk('dt', null, rows[i][0]));
      r.appendChild(mk('dd', null, rows[i][1]));
      dl.appendChild(r);
    }
    box.appendChild(dl);
    for (i = 0; i < notes.length; i++) box.appendChild(mk('p', 'es-note', notes[i]));
    return box;
  }

  // Product Code：单 token 且含数字 -> 等宽料号；长句（如通用提示）保持段落
  function buildCode(list) {
    var codes = [];
    var notes = [];
    for (var i = 0; i < list.length; i++) {
      var t = stripDot(list[i]);
      if (t && /^\S+$/.test(t) && /\d/.test(t) && t.length <= 40) codes.push(t);
      else notes.push(list[i]);
    }
    if (!codes.length) return null;
    var box = mk('div', 'es-codes');
    for (i = 0; i < codes.length; i++) box.appendChild(mk('span', 'es-code', codes[i]));
    for (i = 0; i < notes.length; i++) box.appendChild(mk('p', 'es-note', notes[i]));
    return box;
  }

  // Key Features：图标 + 标题 + 副标题 卡片（v7）
  //   · 图标：按关键字从 FEAT 里挑，认不出用 FEAT_FALLBACK —— 永远不出现空白图标位。
  //   · 标题/副标题：同一行文字里用分隔符切开（默认 ` — `，也可用 | 或 –）。
  //     没有分隔符 → 整行当标题、不生成副标题那一行 → 单行卡。
  //     所以存量的一行内容不会被写坏，只是暂时显示成单行卡。
  // v10：used 是可选参数 = 本商品已用过的图标表 { 图标名: 1 }。
  //   · 传了它 → 返回**第一个还没被用过的**命中图标（款内去重）；
  //   · 所有命中的图标都用过了 → 优先给兜底（若兜底也没用过），否则允许重复；
  //   · 一个都没命中 → 兜底；
  //   · 不传 → 与 v9 完全一致（第一条命中即返回），供测试与预览页直接调用。
  function featIcon(s, used) {
    var first = null;
    for (var i = 0; i < FEAT.length; i++) {
      if (!FEAT[i].re.test(s)) continue;
      if (first === null) first = FEAT[i].ic;
      if (!used || !used[FEAT[i].ic]) return FEAT[i].ic;
    }
    if (first === null) return FEAT_FALLBACK;
    if (used && !used[FEAT_FALLBACK]) return FEAT_FALLBACK;
    return first;
  }

  function buildFeat(list) {
    if (!list.length) return null;
    var box = mk('div', 'es-feat-list');
    // v10 款内去重：记住本商品已经用掉的图标。只影响"挑哪个图标"，文案一个字不动。
    var used = {};
    for (var i = 0; i < list.length; i++) {
      var raw = list[i];
      // ⚠️ 分隔符两侧必须有空白（\s+[—–|]\s+），这样标题里自带竖线的
      //    （如 `Front Fork Rebound Spring … | 65mm Length`）不会被误切。
      var m = CFG.featSep ? CFG.featSep.exec(raw) : null;
      var title, sub = '';
      if (m && m.index > 0 && m.index + m[0].length < raw.length) {
        title = stripDot(raw.slice(0, m.index).trim());
        sub = raw.slice(m.index + m[0].length).trim();
      } else {
        title = stripDot(raw);
      }
      if (!title) continue;   // 分隔符跑到行首之类的怪数据：跳过，不生成空卡

      var card = mk('div', 'es-feat');
      // ⚠️ 记账必须在生成卡片之后、且跳过的卡（!title）不占名额 —— 所以放在 continue 后面。
      var ic = featIcon(raw, CFG.featUnique ? used : null);
      used[ic] = 1;
      card.appendChild(mkIcon(ic));
      var txt = mk('div', 'es-feat-txt');
      txt.appendChild(mk('p', 'es-feat-title', title));
      if (sub) txt.appendChild(mk('p', 'es-feat-sub', sub));
      card.appendChild(txt);
      box.appendChild(card);
    }
    return box.children.length ? box : null;
  }

  // Shipping & After-sales：图标行（v4 起各行同字重，不做局部加粗）
  //   v5：先按 AFT_RE 分桶，能分成两块就渲染成 tab，否则平铺（内容不丢）。
  function shipRow(s) {
    var ic = null;
    for (var k = 0; k < SHIP.length; k++) {
      if (SHIP[k].re.test(s)) { ic = SHIP[k].ic; break; }
    }
    // 认不出图标的条目（实际数据里没有）降级成浅色小字，留在自己所属的面板里
    if (!ic) return mk('p', 'es-note', s);
    var li = mk('li');
    li.appendChild(mkIcon(ic));
    // v4：整行同字重。原来把「冒号前的标签」包 <strong>，但这类条目是少数
    //     （50/214），结果只有 Estimated delivery 一条被加粗，反而突兀。
    //     原文里的冒号、空格一并保留 —— 文本一个字符都不动。
    li.appendChild(mk('span', null, s));
    return li;
  }

  function shipList(lines) {
    var ul = mk('ul', 'es-ship');
    for (var i = 0; i < lines.length; i++) ul.appendChild(shipRow(lines[i]));
    return ul;
  }

  function buildShipping(list) {
    var i;
    var bucket = {};
    for (i = 0; i < TAB.length; i++) bucket[TAB[i].key] = [];
    for (i = 0; i < list.length; i++) bucket[AFT_RE.test(list[i]) ? 'aft' : 'del'].push(list[i]);

    var splittable = true;
    for (i = 0; i < TAB.length; i++) {
      if (!bucket[TAB[i].key].length) splittable = false;
    }

    var box = mk('div', 'es-ship-box');

    // 只有一块内容（或关了开关）→ 退回 v4 的平铺，不硬凑一个空 tab
    if (!CFG.tabShipping || !splittable) {
      var flat = shipList(list);
      if (!flat.children.length) return null;
      box.appendChild(flat);
      return box;
    }

    // ⚠️ 用 <button> + 容器上的 data-tab，而不是 radio + label：
    //    radio 必须靠 id 关联 <label for>，页面里一旦出现第二份描述区就有重复 id，
    //    而 CSS 的 :checked 兄弟选择器只认第一份 —— 第二份会永远切不动。
    //    data-tab 方案全无 id，同一页多份各切各的。
    var tb = mk('div', 'es-tb');
    tb.setAttribute('data-tab', TAB[0].key);

    var head = mk('div', 'es-th');
    for (i = 0; i < TAB.length; i++) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'es-tbtn';
      b.setAttribute('data-go', TAB[i].key);
      b.setAttribute('aria-pressed', TAB[i].key === TAB[0].key ? 'true' : 'false');
      b.appendChild(document.createTextNode(TAB[i].label));
      head.appendChild(b);
    }
    tb.appendChild(head);

    for (i = 0; i < TAB.length; i++) {
      var p = mk('div', 'es-tp');
      p.setAttribute('data-panel', TAB[i].key);
      p.appendChild(shipList(bucket[TAB[i].key]));
      tb.appendChild(p);
    }

    box.appendChild(tb);
    return box;
  }

  var BUILD = {
    fitment: buildFitment,
    specs: buildSpecs,
    code: buildCode,
    shipping: buildShipping
  };

  // v7：Feature 图标卡。整块可关 —— CFG.featCards 改 false 就退回 v6 的项目符号。
  if (CFG.featCards) BUILD.features = buildFeat;

  function shouldOpen(i, title) {
    if (CFG.openFirst && i === 0) return true;
    var t = norm(title).toLowerCase();
    for (var k = 0; k < CFG.openTitles.length; k++) {
      if (norm(CFG.openTitles[k]).toLowerCase() === t) return true;
    }
    return false;
  }

  // 把一个小标题及其后续同级内容包成 <details>
  // 说明：<summary> 里直接放原来的 h2 元素 —— HTML 规范允许 summary 内含 heading，
  //       这样标题会保留主题原有的 h2 字号/字重，视觉上除了多一个 + 号几乎没有变化。
  function fold(root) {
    if (!root || root.nodeType !== 1) return 0;
    // 幂等守卫：已折叠过就跳过。
    // ⚠️ 用 hasAttribute 而不是比对具体值 —— v4 把值从 '1' 改成了 '4'，
    //    写死 '1' 会让 shopify:section:load 的补跑把整段重复折叠一遍。
    if (root.hasAttribute(ATTR)) return 0;
    if (root.querySelector('details.' + CFG.cls)) return 0;

    var kids = [];
    var i;
    for (i = 0; i < root.children.length; i++) kids.push(root.children[i]);

    var lead = [];
    var groups = [];
    var cur = null;

    for (i = 0; i < kids.length; i++) {
      var el = kids[i];
      if (isHeading(el)) {
        cur = { head: el, body: [] };
        groups.push(cur);
      } else if (cur) {
        cur.body.push(el);
      } else {
        // 第一个小标题之前的内容（引导语）留在折叠之外
        lead.push(el);
      }
    }

    if (!groups.length) return 0;

    var frag = document.createDocumentFragment();
    for (i = 0; i < lead.length; i++) frag.appendChild(lead[i]);

    for (var g = 0; g < groups.length; g++) {
      var grp = groups[g];
      var title = norm(grp.head.textContent);
      var key = SEC[title.toLowerCase()] || null;

      var d = document.createElement('details');
      d.className = CFG.cls;

      var s = document.createElement('summary');
      s.appendChild(grp.head);
      d.appendChild(s);

      // 富版式：能整段转换才转换，否则原样搬运
      var rich = null;
      if (CFG.rich && key && BUILD[key]) {
        var list = entries(grp.body);
        if (list) rich = BUILD[key](list);
      }

      if (rich) {
        // ⚠️ 必须把原始条目从文档里摘掉：此时它们还挂在 root 上，
        //    只 append 新节点的话内容会在页面上重复出现一份。
        //    grp.body 的元素可能是 <p> 也可能是整个 <ul>，逐个移除即可。
        for (var k2 = 0; k2 < grp.body.length; k2++) {
          var old = grp.body[k2];
          if (old.parentNode) old.parentNode.removeChild(old);
        }
        d.appendChild(rich);
        d.setAttribute(RICH, key);
      } else {
        for (var j = 0; j < grp.body.length; j++) d.appendChild(grp.body[j]);
      }

      if (shouldOpen(g, title)) d.open = true;
      frag.appendChild(d);
    }

    root.appendChild(frag);
    // v7 起：值即版本号 —— 页面源码搜 data-es-acc="10" 就能确认装的是哪版
    root.setAttribute(ATTR, '10');
    return groups.length;
  }

  function run() {
    var nodes = document.querySelectorAll(CFG.container);
    var n = 0;
    for (var i = 0; i < nodes.length; i++) {
      var c = nodes[i];
      var rte = (c.classList && c.classList.contains('rte')) ? c : c.querySelector('.rte');
      n += fold(rte || c);
    }
    return n;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // 主题编辑器里重新渲染 section 时补跑一次
  document.addEventListener('shopify:section:load', function () {
    run();
  });

  // v5：Shipping 两个 tab 的切换。事件委托挂在 document 上 ——
  //     主题编辑器重渲染 section 后不需要重新绑定，也不会因为多次 run() 重复挂。
  document.addEventListener('click', function (e) {
    var el = e.target;
    if (!el || el.nodeType !== 1 || !el.closest) return;
    var btn = el.closest('.es-tbtn');
    if (!btn) return;
    var head = btn.parentNode;
    var tb = head && head.parentNode;
    if (!tb || !tb.classList || !tb.classList.contains('es-tb')) return;
    var go = btn.getAttribute('data-go');
    if (!go) return;
    tb.setAttribute('data-tab', go);
    var kids = head.children;
    for (var i = 0; i < kids.length; i++) {
      kids[i].setAttribute('aria-pressed', kids[i] === btn ? 'true' : 'false');
    }
  });

  // ⚠️ run / config 两个键被 _acc_test.cjs 用了 5 处，改名会打挂测试。
  //    后面几个是 v7/v8 加的：给预览页和自检脚本读规则表用，线上逻辑不依赖它们。
  window.esDescAccordion = {
    run: run,
    config: CFG,
    version: '10',
    ICONS: ICONS,
    FEAT: FEAT,
    FEAT_FALLBACK: FEAT_FALLBACK,
    featIcon: featIcon,
    splitFitGroup: splitFitGroup,
    mkIcon: mkIcon
  };
})();
